/**
 * Lateness Page — siapa late, berapa kali, berapa minit, monthly aggregated.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Clock, AlertTriangle, Calendar, Loader } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LatenessPage = () => {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [monthIdx, setMonthIdx] = useState(today.getMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedStaff, setExpandedStaff] = useState(null);

  const monthStr = `${year}-${String(monthIdx + 1).padStart(2, '0')}`;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/hr/lateness`, { params: { month: monthStr } });
      setData(data);
    } catch {
      toast.error('Gagal memuat data lateness');
    } finally { setLoading(false); }
  }, [monthStr]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className="space-y-5" data-testid="lateness-page">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800 flex items-center gap-2">
          <Clock className="w-6 h-6 text-red-700" /> Lateness Report
        </h1>
        <p className="text-gray-500 text-xs sm:text-sm">Tracking siapa late, berapa kali, dan berapa minit dalam sebulan.</p>
      </div>

      {/* Month picker */}
      <div className="flex items-center gap-2 bg-white rounded-xl border border-gray-200 p-2 sm:p-3 max-w-sm">
        <Calendar className="w-4 h-4 text-gray-400" />
        <input type="month" value={monthStr} onChange={(e) => {
          const [y, m] = e.target.value.split('-').map(Number);
          setYear(y); setMonthIdx(m - 1);
        }} data-testid="month-picker"
          className="flex-1 text-sm focus:outline-none" />
      </div>

      {/* Summary */}
      {data?.summary && (
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white rounded-xl border border-l-4 border-l-red-500 border-gray-100 p-4">
            <p className="text-xs text-gray-500">Total Insiden</p>
            <p className="text-2xl font-bold text-red-600">{data.summary.total_incidents}</p>
          </div>
          <div className="bg-white rounded-xl border border-l-4 border-l-amber-500 border-gray-100 p-4">
            <p className="text-xs text-gray-500">Total Minit Late</p>
            <p className="text-2xl font-bold text-amber-600">{data.summary.total_minutes}</p>
          </div>
          <div className="bg-white rounded-xl border border-l-4 border-l-blue-500 border-gray-100 p-4">
            <p className="text-xs text-gray-500">Staff Yang Late</p>
            <p className="text-2xl font-bold text-blue-600">{data.summary.unique_staff_late}</p>
          </div>
        </div>
      )}

      {/* Staff list */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-12 flex justify-center"><Loader className="w-6 h-6 animate-spin text-gray-400" /></div>
        ) : !data?.items?.length ? (
          <div className="p-12 text-center text-gray-400">
            <AlertTriangle className="w-10 h-10 mx-auto mb-2" />
            <p className="text-sm">Tiada lateness untuk bulan ini. 🎉</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {data.items.map((s, idx) => {
              const isExpanded = expandedStaff === s.staff_id;
              const isWorst = idx < 3;
              return (
                <div key={s.staff_id || idx} data-testid={`late-${s.staff_id}`}>
                  <button onClick={() => setExpandedStaff(isExpanded ? null : s.staff_id)}
                    className="w-full text-left p-4 sm:p-5 hover:bg-gray-50 flex items-center gap-3">
                    {/* Rank */}
                    <span className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                      isWorst ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
                    }`}>#{idx + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-800 truncate">{s.staff_name}</p>
                      <p className="text-xs text-gray-500">{s.department || '—'}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-gray-800">{s.total_late_minutes}<span className="text-xs text-gray-500 font-normal"> min</span></p>
                      <p className="text-[11px] text-gray-500">{s.total_late_count}× insiden</p>
                    </div>
                  </button>
                  {isExpanded && s.incidents?.length > 0 && (
                    <div className="px-4 sm:px-5 pb-4 bg-gray-50">
                      <div className="text-[11px] font-bold tracking-wider text-gray-500 uppercase mb-2">Incidents</div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {s.incidents.map((inc, i) => (
                          <div key={i} className="flex items-center justify-between bg-white rounded-lg p-2 text-xs">
                            <span className="text-gray-700">{inc.date}</span>
                            <span className="text-gray-500">@ {inc.check_in || '—'}</span>
                            <span className="font-bold text-red-600">+{inc.minutes} min</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default LatenessPage;
